<template>
    <header
        class="main-header | z-40 sticky bg-white shadow-md | transition-spacing duration-300 | print:hidden"
        :class="showSearch ? 'h-32' : 'h-18'"
        :style="{ top: `${bodySpacingTop}px` }"
        v-if="!$browser.isMobileWebView()"
    >
        <div class="h-18 flex px-4">
            <div
                class="flex items-center | md:mr-6 md:flex-1"
                data-intercom-target="dashboard.searchAdd"
            >
                <button
                    class="btn-nav sidebar-toggle | pl-0 shrink-0 | md:pl-2"
                    type="button"
                    @click="toggleNav"
                    v-if="showMenuButton"
                >
                    <icon
                        :class="mdLayout ? 'text-3xl' : 'text-2xl'"
                        name="regular/navigation-menu"
                    />
                </button>

                <div class="btn-nav px-3" v-if="mdLayout">
                    <icon
                        class="text-2xl"
                        name="regular/search"
                        :class="{ 'text-blue-600': showSearch }"
                        @click="toggleShowSearch"
                    />
                </div>
                <div
                    class="flex-1 ml-4 | md:max-w-[33rem]"
                    data-testid="main-header-search-wrapper"
                    v-if="!mdLayout"
                >
                    <main-search v-if="showHeaderElement('search')" />
                </div>
                <div v-if="xxlLayout">
                    <button
                        data-testid="main-header-add-lead-button"
                        class="link-blue ml-6 flex items-center group"
                        @click="openLeadCreate"
                        v-if="showAddLead"
                    >
                        <icon
                            class="text-sm text-blue-500 group-hover:text-blue-600"
                            :name="$icons.add"
                        />
                        <span
                            class="ml-2 uppercase tracking-wider text-grey-500 group-hover:text-grey-600"
                            v-text="addLeadLabel"
                        />
                    </button>
                </div>
            </div>

            <div class="flex-1 flex items-center justify-end overflow-hidden | xl:flex-initial">
                <slot>
                    <template v-if="showHeaderElement('notification')">
                        <router-link
                            class="btn-nav | md:mr-2"
                            :class="{ 'px-3': mdLayout }"
                            data-intercom-target="dashboard.calendar"
                            :to="{ name: 'calendar' }"
                            v-if="authUser.hasAccessTo('calendar')"
                        >
                            <icon
                                :class="mdLayout ? 'text-2xl' : 'text-lg'"
                                name="regular/calendar"
                            />
                        </router-link>
                        <notification-center />
                    </template>
                    <router-link
                        class="btn-nav | md:mr-2"
                        :class="{ 'px-3': mdLayout }"
                        :to="{
                            name: 'accounts.update',
                            params: { id: contextAccount.id },
                        }"
                        v-if="authUser.isAdmin() && contextAccount.id"
                    >
                        <icon :class="mdLayout ? 'text-2xl' : 'text-lg'" name="regular/shop" />
                    </router-link>
                    <div v-if="xxlLayout">
                        <teams class="ml-2" v-if="showHeaderElement('team') && showTeams" />
                    </div>
                    <divisions class="ml-2" v-if="showHeaderElement('division')" />
                    <group-account-menu
                        class="ml-2 shrink"
                        :class="{ 'mr-4': mdLayout }"
                        v-if="showHeaderElement('account')"
                    />
                    <user-dropdown
                        data-intercom-target="dashboard.preferences"
                        class="md:ml-2"
                        v-if="showHeaderElement('settings')"
                    />
                </slot>
            </div>
        </div>
        <div class="h-18 px-4" v-if="showSearch">
            <main-search
                class="w-full"
                ref="search"
                @submit="showSearch = false"
                v-if="showHeaderElement('search')"
            />
        </div>
    </header>
</template>

<script>
    import { get } from 'lodash-es';
    import { mapState, mapActions } from 'pinia';
    import { isTouchDevice } from '@/utils/device.js';
    import { useEventBus } from '@/hooks/useEventBus.ts';

    // Components
    import Divisions from './Divisions.vue';
    import GroupAccountMenu from './GroupAccountMenu.vue';
    import MainSearch from './MainSearch.vue';
    import NotificationCenter from './NotificationCenter.vue';
    import Teams from './Teams.vue';
    import UserDropdown from './nav/UserDropdown.vue';

    // Entities
    import DashboardType from '../entities/DashboardType.js';

    // Pinia
    import { useDashboardStore } from '../store/modules/dashboard/store.js';
    import { useLayoutStore } from '../store/modules/layout/store.js';
    import { useAccountStore } from '../store/modules/account/store.js';
    import { useUserStore } from '../store/modules/user/store.js';

    const { emit } = useEventBus();

    export default {
        components: {
            Divisions,
            GroupAccountMenu,
            MainSearch,
            NotificationCenter,
            Teams,
            UserDropdown,
        },

        data() {
            return {
                showSearch: false,
            };
        },

        computed: {
            ...mapState(useDashboardStore, ['dashboardType']),
            ...mapState(useLayoutStore, ['bodySpacingTop']),

            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            showTeams() {
                return (
                    !!this.contextAccount.activeTeams.length &&
                    (this.authUser.isAdmin() ||
                        this.authUser.isDirector() ||
                        this.authUser.isBdcDirector())
                );
            },

            showAddLead() {
                return (
                    this.contextAccount.id &&
                    this.dashboardType != DashboardType.EVENT &&
                    this.includeInLayout('nav')
                );
            },

            showMenuButton() {
                if (!this.includeInLayout('nav')) {
                    return false;
                }

                return isTouchDevice();
            },

            addLeadLabel() {
                switch (this.dashboardType) {
                    case DashboardType.WALK_IN:
                        return this.$t('general.walkIn');

                    case DashboardType.PHONE_UP:
                        return this.$t('general.phoneUp');

                    case DashboardType.ALL:
                    case DashboardType.RENEWAL:
                    case DashboardType.COMMERCIAL:
                    case DashboardType.LOYALTY:
                    case DashboardType.SERVICE:
                    case DashboardType.SALE:
                    case DashboardType.EVENT:
                        return this.$t('general.client', 1);

                    default:
                        return this.$t('general.lead', 1);
                }
            },
        },

        watch: {
            '$route.path'() {
                if (this.showSearch && this.mdLayout) {
                    this.showSearch = false;
                }
            },
        },

        methods: {
            ...mapActions(useLayoutStore, ['setHeaderHeight']),

            openLeadCreate() {
                emit('open-lead-create', {
                    accountId: this.contextAccount.id,
                    type: this.dashboardType ? this.underscore(this.dashboardType) : null,
                });
            },

            showHeaderElement(element) {
                return get(this.$route.meta, `header.${element}`, false);
            },

            toggleNav() {
                emit('toggle-nav');
            },

            async toggleShowSearch() {
                this.showSearch = !this.showSearch;

                await this.$nextTick();

                if (this.showSearch) {
                    this.$refs.search.$refs.mainSearch.$refs.input.focus();
                }
            },

            async onHeaderMutation() {
                await this.$nextTick();

                this.setHeaderHeight(this.$el.offsetHeight);
            },
        },

        mounted() {
            this.headerObserver = new MutationObserver(this.onHeaderMutation);
            this.headerObserver.observe(this.$el, {
                attributeFilter: ['class'],
            });
            this.onHeaderMutation();
        },

        beforeUnmount() {
            this.headerObserver.disconnect();
            this.headerObserver = null;
        },
    };
</script>

<style lang="less">
    .main-header {
        .btn-nav {
            @apply relative p-2 border-0 bg-transparent text-grey-700 flex items-center;

            &:hover {
                @apply text-grey-800;
            }

            .badge {
                @apply absolute top-0 right-0;
                margin-right: -2px;

                &.badge-empty {
                    margin-top: 5px;
                    margin-right: 5px;
                }
            }
        }

        .nav-select {
            [data-testid='select-trigger'] {
                @apply text-lg pl-0 bg-transparent border-transparent;

                box-shadow: none;
            }

            [data-testid='select-label'],
            [data-testid='select-placeholder'] {
                @apply text-grey-700;
            }

            [data-testid='select-trigger'] svg {
                @apply text-grey-600;
            }

            [data-testid='select-trigger']:hover,
            [data-testid='select-trigger']:focus,
            [data-testid='select-trigger']:focus-visible {
                @apply bg-transparent border-transparent ring-0;

                box-shadow: none;
            }

            [data-testid='select-trigger']:hover [data-testid='select-label'],
            [data-testid='select-trigger']:hover [data-testid='select-placeholder'],
            [data-testid='select-trigger']:focus [data-testid='select-label'],
            [data-testid='select-trigger']:focus [data-testid='select-placeholder'],
            [data-testid='select-trigger']:focus-visible [data-testid='select-label'],
            [data-testid='select-trigger']:focus-visible [data-testid='select-placeholder'] {
                @apply text-grey-800;
            }

            [data-testid='select-trigger']:hover svg,
            [data-testid='select-trigger']:focus svg,
            [data-testid='select-trigger']:focus-visible svg {
                @apply text-grey-700;
            }
        }
    }
</style>
